import { useCallback, useEffect, useState } from 'react';
import { ContentKeywordGq } from '../../../../graphql/projects/models/ContentKeywordGq';
import { copyToClipboard } from '../../../../logic/otherUtilities/copyTextToClipboard';
import { ContentTaskTableState } from '../Tables/ContentKeywordTable/logic/useContentTaskTableState';

export interface CopyOptions {
  tableState: ContentTaskTableState;
  contentKeywords: ContentKeywordGq[];
  selectedIds: number[];
}

export function useCopy(options: CopyOptions) {
  const { tableState, selectedIds, contentKeywords } = options;
  // Snackbars
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState<string>();

  // Hide snackbar after a while
  useEffect(() => {
    if (!showSnackbar) {
      return;
    }

    const timeout = setTimeout(() => setShowSnackbar(false), 1250);
    return () => clearTimeout(timeout);
  }, [showSnackbar]);

  // Hide error message after a while
  useEffect(() => {
    if (!errorSnackbarMessage?.length) {
      return;
    }
    const timeout = setTimeout(() => setErrorSnackbarMessage(undefined), 5000);
    return () => clearTimeout(timeout);
  }, [errorSnackbarMessage]);

  // Copy function
  const handleCopy = useCallback(() => {
    try {
      // Find selected keywords
      const selectedIdsMap: Record<number, boolean> = {};
      selectedIds.forEach((id) => (selectedIdsMap[id] = true));
      const selectedKeywords = contentKeywords.filter(
        (ck) => selectedIds.length === 0 || selectedIdsMap[ck.id]
      );

      // Prepare copy text
      const copyText = selectedKeywords.map((kw) => kw.keyword).join('\n');

      // Copy result
      const copyResult = copyToClipboard(copyText);

      // Show feedback
      if (copyResult) {
        setShowSnackbar(true);
      } else {
        setErrorSnackbarMessage('Copy not supported by browser - try Chrome instead');
      }

      // Close down
      tableState.handleResetSelection();
    } catch {
      // Show eventual error
      setErrorSnackbarMessage('Copy error - something went wrong');
    }
  }, [selectedIds, contentKeywords]);

  // Keyword shortcut handlers
  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      // Don't copy if some kind of input element is focused
      if (
        ['input', 'select', 'textarea', 'p'].includes(document.activeElement?.tagName?.toLowerCase())
      ) {
        if (
          document.activeElement.tagName.toLowerCase() === 'input' &&
          document.activeElement.attributes.getNamedItem('type')?.value?.toLowerCase() ===
          'checkbox'
        ) {
          // This is probably the selection checkbox being pressed...
        } else {
          // ... Don't copy, we don't know if the user is in an input field or something....
          return;
        }
      }

      // Make sure the right buttons are selected
      if (event.key === 'c' && (event.metaKey || event.ctrlKey)) {
        // Make sure to close the popover if it's open
        // setMenuAnchor(null);

        // Execute copy
        handleCopy();
      }
    };

    // Add/remove handler
    window.addEventListener('keydown', keyDownHandler, false);
    return () => window.removeEventListener('keydown', keyDownHandler);
  }, [handleCopy]);

  return {
    handleCopy,
    showSnackbar,
    errorSnackbarMessage,
    setShowSnackbar,
    setErrorSnackbarMessage,
  };
}
